import { Button, Accordion, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDeviceFloppy, IconHistory } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import RotationInfoModal from "src/components/pages/Settings/CrewSetUp/RotationInfoModal/RotationInfoModal";
import AssignedLogbooks from "src/components/pages/Settings/CrewSetUp/UserForm/AssignedLogbooks";
import CrewAuthButton from "src/components/pages/Settings/CrewSetUp/UserForm/CrewAuthButton";
import Details from "src/components/pages/Settings/CrewSetUp/UserForm/Details";
import AdditionalInfo from "src/components/pages/Settings/CrewSetUp/UserForm/AdditionalInfo";
import SendMailButton from "src/components/pages/Settings/CrewSetUp/UserForm/SendMailButton";
import SetCredentialsButton from "src/components/pages/Settings/CrewSetUp/UserForm/SetCredentialsButton";
import CheckboxGroup from "src/components/shared/_FormInputs/CheckboxGroup/CheckboxGroup";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { useCheckPerm, useGetApi, usePostApi } from "src/hooks";
import { useSelector } from "src/redux/hooks";
import { ButtonsWrapper, Flex, PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { LayoutContainer } from "../../../../styled/UserForm.styled";
import { type IUserForm } from "./UserForm.types";
import { mapUserForm, mapUserSelects } from "./UserForm.utils";
import { useTheme } from "src/hooks";

interface Props {
  isManagement?: boolean;
  isCreateUser?: boolean;
}

const UserForm = ({ isManagement = false, isCreateUser = false }: Props) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const checkPerm = useCheckPerm();
  const { post, isLoading } = usePostApi();

  const clientid = useSelector(state => state.permsSlice.clientid);

  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string | null>(
    "details",
  );
  const { colors } = useTheme();
  const { data, setData } = useGetApi({
    request: isManagement ? "user-form-man" : "user-form",
    body: {
      params: { userid: userId ?? "new", yachtid: getYachtId() },
    },
    mapper: mapUserForm,
  });
  const [searchParams] = useSearchParams();
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    formState,
    getValues,
  } = useForm<IUserForm>({
    values: data?.initialValues,
  });

  const updatePerms = useCallback(
    (data: any) => {
      const result = mapUserSelects(data);
      const perms = [
        ...result.userPerms.map(perm => perm.value),
        ...result.adminPerms.map(perm => perm.value),
      ];
      setValue("perms", perms);
      return result;
    },
    [setValue],
  );

  const { data: selectsData, refetch: refetchSelects } = useGetApi({
    request: "user-form-man-type",
    mapper: updatePerms,
    fetchOnInit: false,
  });
  const handleTypeChange = async (type: string) => {
    await refetchSelects({ params: { type } });
  };
  // const handleRankChange = async (role: string) => {
  //   console.log(role);
  //   if (role === MEDRANK && data) {
  //     if (data.selects.logbooks.find(lb => lb.value === MEDLBID)) return;
  //     const newLbs = deepClone(data.selects.logbooks);
  //     newLbs.push({ label: "Medical Log", value: MEDLBID });
  //     console.log(newLbs);
  //     setData(
  //       deepClone({
  //         ...data,
  //         selects: { ...data.selects, logbooks: newLbs },
  //       }),
  //     );
  //   }
  // };
  const submit: SubmitHandler<IUserForm> = async data => {
    const reqParams: Record<string, string> = {};
    const params = Object.fromEntries(searchParams.entries());

    ["orgid", "yachtid", "clientid"].forEach(param => {
      if (params[param] && params[param] !== "NULL") {
        reqParams[param] = params[param];
      } else if (param === "yachtid") {
        reqParams.yachtid = getYachtId();
      } else if (param === "clientid" && clientid) {
        reqParams.clientid = clientid;
      }
    });

    await post({
      request: "save-user",
      body: {
        params: {
          userid: userId ?? "new",
          ...reqParams,
          ...data,
        },
      },
      onSuccess: async res => {
        if (res?.requiresOverride) {
          // OPEN ROTATION INFO MODAL WHEN REQUIRES OVERRIDE
          open();
        } else {
          reset(data);
          Notification(
            "Success",
            `User ${userId ? "updated" : "created"} successfully.`,
            "green",
          );

          if (!userId) {
            if (isCreateUser && sendWelcomeEmail && res?.newUserId) {
              await post({
                request: "send-welcome",
                body: { params: { userid: res?.newUserId } },
              });
            }

            if (isManagement) navigate("/management/users");
            else navigate("/settings/userlist");
          }
        }
      },
    });
  };

  const onSubmit = async (authpw: string) => {
    const result = await trigger();
    if (result) {
      setValue("authpw", authpw);
      await handleSubmit(submit)();
    } else {
      Notification("Validation Error", "Fill in the required fields", "red");
    }
  };

  const disabled = !!(userId && !checkPerm("editeuser"));
  // const selects = useMemo(() => {
  //     const adminPerms =
  //       (selectsData ? selectsData.adminPerms : data?.selects.adminPerms) ?? [];
  //     return {
  //       ranks: (selectsData ? selectsData?.roles : data?.selects.roles) ?? [],
  //       userPerms:
  //         (selectsData ? selectsData.userPerms : data?.selects.userPerms)?.map(
  //           el => {
  //             if (adminPerms.length > 0) return el;
  //
  //             const copy = deepClone(el);
  //             if (copy.value.includes("edit") || copy.value.includes("delete")) {
  //               copy.value = "";
  //             }
  //             return copy;
  //           },
  //         ) ?? [],
  //       adminPerms,
  //     };
  //   }, [selectsData, data]);

  // const parsedSelectsData = useMemo(
  //   () => ({
  //     ...selectsData,
  //     userPerms:
  //       selectsData?.userPerms?.map(el => {
  //         if (selectsData.adminPerms.length > 0) return el;
  //         const copy = deepClone(el);
  //         if (copy.value.includes("edit") || copy.value.includes("delete")) {
  //           copy.value = "";
  //         }
  //         return copy;
  //       }) ?? [],
  //     adminPerms: selectsData?.adminPerms ?? [],
  //   }),
  //   [selectsData],
  // );

  const selects = useMemo(
    () => ({
      ranks: (selectsData ? selectsData?.roles : data?.selects.roles) ?? [],
      userPerms:
        (selectsData ? selectsData.userPerms : data?.selects.userPerms) ?? [],
      adminPerms:
        (selectsData ? selectsData.adminPerms : data?.selects.adminPerms) ?? [],
    }),
    [selectsData, data],
  );
  const [opened, { open, close }] = useDisclosure(false);
  const userType = getValues("type") as unknown as number;

  const [crewDisplayName, setCrewDisplayName] = useState("");
  const prevTab = useRef(accordionValue);
  const loaded = useRef(false);

  const watchedFname = useWatch({ control, name: "fname" });
  const watchedSname = useWatch({ control, name: "sname" });

  useEffect(() => {
    if (!loaded.current && (watchedFname || watchedSname)) {
      loaded.current = true;
      setCrewDisplayName(`${watchedFname ?? ""} ${watchedSname ?? ""}`.trim());
    }
  }, [watchedFname, watchedSname]);

  useEffect(() => {
    if (prevTab.current === "details" && prevTab.current !== accordionValue) {
      setCrewDisplayName(`${watchedFname ?? ""} ${watchedSname ?? ""}`.trim());
    }
    prevTab.current = accordionValue;
  }, [accordionValue]);

  const accOpenStyles = (v: string): React.CSSProperties => {
    if (v === "user") {
      return {
        display: "flex",
        alignItems: "center",

        backgroundColor: colors.main[1],
        color: colors.text,
        cursor: "default",
        marginBottom: 32,
        padding: "16px 20px 16px",
        borderRadius: 4,
      };
    }
    return accordionValue === v
      ? { borderColor: colors.main[9] }
      : {
          backgroundColor: colors.selectItemHoverBg,
        };
  };

  return (
    <>
      <RotationInfoModal
        opened={opened}
        close={close}
        refetch={refetchSelects}
      />
      <PageContainer>
        <Form
          control={control}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
          }}>
          <Breadcrumbs w="100%">
            <ButtonsWrapper>
              {userId && (
                <>
                  <SendMailButton />
                  <Button
                    leftIcon={<IconHistory size={"1.4rem"} />}
                    variant="outline"
                    onClick={() =>
                      navigate(`/settings/userlist/edit-user/${userId}/history`)
                    }>
                    History
                  </Button>
                  <SetCredentialsButton userId={userId} />
                </>
              )}
              {!disabled && (
                <CrewAuthButton
                  onSubmit={onSubmit}
                  leftIcon={<IconDeviceFloppy size={"1.4rem"} />}
                  variant="outline"
                  loading={isLoading}>
                  Save
                </CrewAuthButton>
              )}
            </ButtonsWrapper>
          </Breadcrumbs>
          <Flex w="100%" h="calc(100% - 52px)" gap={20} justify="flex-start">
            <LayoutContainer
              style={{
                maxWidth: "300px",
                padding: 0,
              }}>
              <Accordion
                defaultValue="details"
                w="100%"
                variant="separated"
                p={20}
                value={accordionValue}
                onChange={v => v && setAccordionValue(v)}
                chevron={null}>
                <Flex style={{ ...accOpenStyles("user") }}>
                  {crewDisplayName ? crewDisplayName : "New User"}
                </Flex>
                <Accordion.Item
                  value="details"
                  style={accOpenStyles("details")}>
                  <Accordion.Control>Details</Accordion.Control>
                </Accordion.Item>

                <Accordion.Item
                  value="logbooks"
                  style={accOpenStyles("logbooks")}>
                  <Accordion.Control disabled={userType === 10}>
                    Assigned Logbooks
                  </Accordion.Control>
                </Accordion.Item>

                <Accordion.Item
                  value="permissions"
                  style={accOpenStyles("permissions")}>
                  <Accordion.Control disabled={userType === 10}>
                    Permissions
                  </Accordion.Control>
                </Accordion.Item>

                <Accordion.Item
                  value="additional"
                  style={accOpenStyles("additional")}>
                  <Accordion.Control>Additional Info</Accordion.Control>
                </Accordion.Item>
              </Accordion>
            </LayoutContainer>
            <LayoutContainer
              style={{
                flex: 1,
                overflow: "auto",
                padding: 0,
              }}>
              <Accordion
                defaultValue="details"
                w="100%"
                variant="filled"
                value={accordionValue}
                onChange={setAccordionValue}
                transitionDuration={0}>
                <Accordion.Item
                  value="details"
                  style={{
                    border: "none",
                    background: "none",
                    ...(accordionValue === "details"
                      ? { padding: "15px 4px 4px" }
                      : {}),
                    margin: 0,
                  }}>
                  <Accordion.Panel>
                    <Details
                      register={register}
                      control={control}
                      onTypeChange={handleTypeChange}
                      types={data?.selects.types || []}
                      ranks={selects.ranks}
                      errors={formState.errors}
                    />
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item
                  value="logbooks"
                  style={{ border: "none", background: "none", margin: 0 }}>
                  <Accordion.Panel>
                    <AssignedLogbooks
                      getValues={getValues}
                      setValue={setValue}
                      control={control}
                      logbooks={data?.selects.logbooks ?? []}
                      disabled={disabled}
                      noScroll
                    />
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item
                  value="permissions"
                  style={{ border: "none", background: "none", margin: 0 }}>
                  <Accordion.Panel>
                    <div
                      style={{
                        padding: "15px 4px 4px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 20,
                        alignItems: userType ? "stretch" : "center",
                      }}>
                      {!userType && (
                        <Text c="dimmed" pt={20} fs="italic">
                          First select a user type in the Details tab.
                        </Text>
                      )}
                      {selects.adminPerms.length > 0 && (
                        <CheckboxGroup
                          title="Admin"
                          data={selects.adminPerms}
                          control={control}
                          name="perms"
                          disabled={disabled}
                          renderLabel={perm => (
                            <>
                              <span style={{ flex: 1, paddingRight: 2 }}>
                                {perm.label}
                              </span>
                              <span style={{ flex: 1.5 }}>
                                {perm.description}
                              </span>
                            </>
                          )}
                        />
                      )}
                      {selects.userPerms.length > 0 && (
                        <CheckboxGroup
                          title="User"
                          data={selects.userPerms}
                          control={control}
                          name="perms"
                          disabled={disabled}
                          renderLabel={perm => (
                            <>
                              <span style={{ flex: 1, paddingRight: 2 }}>
                                {perm.label}
                              </span>
                              <span style={{ flex: 1.5 }}>
                                {perm.description}
                              </span>
                            </>
                          )}
                        />
                      )}
                    </div>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item
                  value="additional"
                  style={{
                    border: "none",
                    background: "none",
                    margin: 0,
                    ...(accordionValue === "additional"
                      ? { padding: "15px 4px 4px" }
                      : {}),
                  }}>
                  <Accordion.Panel>
                    <AdditionalInfo
                      control={control}
                      register={register}
                      errors={formState.errors}
                      setValue={setValue}
                      getValues={getValues}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </LayoutContainer>
          </Flex>
        </Form>
      </PageContainer>
    </>
  );
};

export default UserForm;
